import { useQuery } from '@tanstack/react-query';
import { get } from '@truefit/http-utils';

import { isTmStackActive } from '../tmStacks';

export interface TestLabFeed {
  feed: string;
  /** Gateway port an encoder pushes into. */
  inputPort: number;
  /** Gateway port the TM stack dials. */
  outputPort: number;
}

export interface TestLabStack {
  feed: string;
  configUuid: string;
  stackUuid: string | null;
  state: string;
  /**
   * TM's verbatim reason for the last failed deployment — usually the raw AWS
   * error. Null when there is none.
   *
   * Describes the *last* attempt, not the current state: TM keeps it on a
   * config that has since relaunched fine, exactly as it keeps `stackUuid`.
   * Only show it when the feed is not running.
   */
  deploymentFailure: string | null;
  /**
   * Where TM writes this feed's HLS: the bucket, and the config's `folder` with
   * `${EVENT}` resolved — `encoded/live/TESTLAB_cam1` rather than the template.
   *
   * Fixed when the config is registered rather than polled, so unlike `state`
   * these do not go stale. Null when the config declares no s3 egress.
   */
  egressBucket: string | null;
  egressPrefix: string | null;
}

export interface TestLabStatus {
  hostname: string;
  /** pending | running | stopping | stopped | unreachable | unknown */
  gatewayState: string;
  gatewayIpAddress: string | null;
  instanceId: string | null;
  /**
   * Why the gateway read failed, when it did. The rest of the response is still
   * valid — the TM half needs no AWS access — so this is a warning, not a load
   * failure.
   */
  gatewayError?: string;
  feeds: TestLabFeed[];
  stacks: TestLabStack[];
  /**
   * The dummy game the lab's output plays through, or null when
   * scripts/create-test-lab-game.ts has never been run.
   *
   * `status` and `startTimestamp` are both owned by the lab and both change
   * under a running one: kirby/launch-ready-gate writes `live` and stamps the
   * start of the live window once every stack reports running, and
   * kirby/testlab-stop returns the status to `recorded` once they are down.
   */
  game: {
    id: number;
    uniqueId: string;
    name: string | null;
    streamId: string | null;
    startTimestamp: string | null;
    status: string | null;
  } | null;
}

/** Gateway states where something is up or on its way up. */
const ACTIVE_GATEWAY_STATES = ['pending', 'running', 'stopping'];

export const isGatewayActive = (state?: string) =>
  !!state && ACTIVE_GATEWAY_STATES.includes(state);

/**
 * Kept under its established name; the rule itself lives in `tmStacks.ts` now
 * that Simulive reads TM states too.
 */
export const isStackActive = isTmStackActive;

/** Whether anything in the lab is up, and therefore worth polling fast for. */
const isLabBusy = (status?: TestLabStatus) =>
  isGatewayActive(status?.gatewayState) ||
  (status?.stacks ?? []).some((stack) => isStackActive(stack.state));

/**
 * Polls the Test lab's live state. Nothing is persisted server-side, so every
 * poll is a fresh read of EC2 and TM — booting the gateway takes 30-60s and TM
 * stacks take minutes.
 *
 * Unlike the sibling hooks, which read a single DB row, each poll costs a DNS
 * lookup, an EC2 DescribeInstances and a TM /list over every config in the
 * account. So poll fast only while something is up, and back right off once the
 * lab is idle — which is most of the time, and is when a tab is likeliest to be
 * left open for hours.
 *
 * Both cadences are deliberately slower than the transitions they watch: the
 * gateway takes 30-60s to boot and a TM stack takes minutes, so a tighter loop
 * only re-reads the same state. The scheduler polls TM independently while a
 * launch is in flight, and these requests add to that.
 *
 * `starting` forces the fast cadence for the window a launch has been queued but
 * has not yet shown up in the data. It is needed because the lab still *looks*
 * idle then: the start endpoint only queues a job, so the gateway reads
 * `stopped` until the scheduler reaches EC2. Left on the idle cadence, the
 * caller would wait up to a minute to learn the lab it just started is up.
 */
export const useTestLabQuery = (starting = false) =>
  useQuery({
    queryKey: ['testLabStatus'],
    queryFn: async () => {
      const { data } = await get<TestLabStatus>('/v1.0/test-lab/gateway');
      return data;
    },
    refetchInterval: (query) =>
      starting || isLabBusy(query.state.data) ? 10000 : 60000,
  });
